// R2/G7, ported verbatim (WS-C §6.1) — the three outcomes of the `/v1/auth/me/grants` fetch,
// as pure functions. A COPY into a new package, never a shared import (D-16); the only edits
// are the import paths.
//
// ⛔ DO NOT SIMPLIFY IT. These are the branches that decide whether an operator is told "you
// have none", "we couldn't tell" or "your list is too big", and getting one wrong is an
// authorization-visibility defect (D-10). Branch logic that matters belongs somewhere it can
// be asserted directly, not interleaved with React timing where a test has to win a race to
// reach it.
import type { PermissionSnapshot, ResourceGrantSnapshot } from '@zion/sdk-react';
// ⛔ From `@zion/sdk-contracts` (`grant-principal.ts:78`) DIRECTLY. That package is NOT fenced, so
// routing this type through the `src/platform/` adapter buys nothing and obscures where it lives —
// and the alternative a reader reaches for, re-declaring a second `{grants, truncated}` shape,
// silently drifts from the producer. C2.
import type { ResourceGrantSnapshotResponse } from '@zion/sdk-contracts';
import type { GrantsError } from './ZeroAccessPanel';

/**
 * The pair that MUST travel together. A `grants: null` with no `error` means "not asked yet";
 * with an `error` it means "asked, and here is why you cannot rely on it". Split them and
 * those two become indistinguishable, which is the `absent → denied` collapse this release
 * exists to delete.
 */
export interface GrantsState {
  readonly grants: readonly ResourceGrantSnapshot[] | null;
  readonly error: GrantsError | null;
}

/** Not asked yet. The only state in which "checking…" is honest. */
export const GRANTS_UNKNOWN: GrantsState = { grants: null, error: null };

/** Signed out: there is nothing to ask for, so this is "loaded, none" — not "unknown". */
export const GRANTS_NONE: GrantsState = { grants: [], error: null };

/**
 * A fetched `GrantsState`, TAGGED with the identity it was fetched FOR.
 *
 * The tag is the whole point. `null` is not enough to mean "not known yet", because a value
 * fetched for operator A is equally not-known for operator B — and B's is the answer being
 * rendered.
 */
export interface FetchedGrants {
  /** The `auth.operatorId` in force when this fetch was issued. */
  readonly forOperatorId: string | null;
  readonly state: GrantsState;
  /**
   * The failure of the last re-read when `state` is the previous usable list for this identity,
   * else `null`. The console names it beside the kept list (R-015 item 4 of
   * 2026-10-20-cp-permissioning), so a failed re-read never reads as a fresh answer.
   */
  readonly rereadError: GrantsError | null;
}

/**
 * Decide which `GrantsState` may be SHOWN, given the live auth state and whatever has been
 * fetched so far.
 *
 * ⚠️ This is a RENDER-TIME derivation and it must stay one. The equivalent "reset the stored
 * state from an effect" shape is a live defect in two directions, because React flushes
 * passive effects AFTER the commit — so the value in hand during the commit that first
 * renders the authenticated tree is always the PREVIOUS one:
 *
 *   1. `useAuth` reports `isAuthenticated: false` on its first render and flips
 *      asynchronously, so the previous value is the unauthenticated branch's `GRANTS_NONE` —
 *      `grants: []`, "loaded, none". A zero-role operator was therefore shown "You don't have
 *      access to any sections yet" BEFORE anyone had asked: `absent → denied`, transiently,
 *      which is the same defect as permanently.
 *   2. Worse, on an IDENTITY change the previous value is the PREVIOUS OPERATOR'S grants, so
 *      `useHasPermission(p, resourceId)` answered `true` for their resources — an over-permit,
 *      not a fail-closed.
 *
 * Both windows close by construction here: untagged or mis-tagged state is unshowable.
 *
 * ⛔ There is deliberately NO effect anywhere that resets the fetched state to unknown.
 */
export function visibleGrantsState(
  isAuthenticated: boolean,
  operatorId: string | null,
  fetched: FetchedGrants | null,
): GrantsState {
  if (!isAuthenticated) return GRANTS_NONE;
  if (fetched === null || fetched.forOperatorId !== operatorId) return GRANTS_UNKNOWN;
  return fetched.state;
}

/**
 * Map a successful response to state.
 *
 * ⚠️ A truncated expansion yields `grants: null` — the partial array that DID arrive is
 * DISCARDED, not applied. Applying it would make `useHasPermission(p, id)` answer `false` for
 * a permission the operator holds, and a wrongly-disabled control is indistinguishable from a
 * real denial. An error surface says "we couldn't determine your access", which is true.
 */
export function grantsStateFromResponse(res: ResourceGrantSnapshotResponse): GrantsState {
  if (res.truncated) {
    return {
      grants: null,
      error: { kind: 'truncated', message: 'The expansion exceeded the response cap.' },
    };
  }
  return { grants: res.grants, error: null };
}

/**
 * Map a failed fetch to state.
 *
 * ⚠️ Never `[]`. `[]` means "you have no grants" and would silently disable every
 * resource-gated control while looking entirely correct.
 */
export function grantsStateFromError(e: unknown): GrantsState {
  return {
    grants: null,
    error: {
      kind: 'fetch',
      message: e instanceof Error ? e.message : 'Could not load your access.',
    },
  };
}

/**
 * Store one grants answer, given the answer stored before it.
 *
 * A failed fetch for the same identity keeps the last usable list, and stores the failure in
 * `rereadError` beside it. The re-read follows a write such as `POST /v1/apps`, and one transient
 * failure there must not drop every grant the console already held for this operator. Every other
 * answer replaces the stored one with `rereadError: null`: the first answer for an identity, an
 * answer for another identity, a truncated expansion, and every usable list.
 */
export function settleFetchedGrants(
  previous: FetchedGrants | null,
  forOperatorId: string | null,
  next: GrantsState,
): FetchedGrants {
  if (
    next.grants === null &&
    next.error?.kind === 'fetch' &&
    previous !== null &&
    previous.forOperatorId === forOperatorId &&
    previous.state.grants !== null
  ) {
    return { forOperatorId, state: previous.state, rereadError: next.error };
  }
  return { forOperatorId, state: next, rereadError: null };
}

/**
 * The failure of the last grants re-read that may be SHOWN beside the kept list, or `null`.
 *
 * A render-time derivation with the identity rule of {@link visibleGrantsState}: signed out, or a
 * store tagged for another operator, gives `null`. Caller: `CpAdminApp`, which also hides it while
 * the next re-read runs, so an old failure never reads as the answer of the new read.
 */
export function visibleGrantsRereadError(
  isAuthenticated: boolean,
  operatorId: string | null,
  fetched: FetchedGrants | null,
): GrantsError | null {
  if (!isAuthenticated || fetched === null || fetched.forOperatorId !== operatorId) return null;
  return fetched.rereadError;
}

/**
 * Build the `PermissionProvider` snapshot the whole UI reads through `useHasPermission`.
 *
 * Extracted from the composition root's `useMemo` so the seam can be asserted directly: this
 * is where a fetched grant either does or does not become an answer to
 * `useHasPermission(permission, resourceId)`. Inside a memo the only way to test it is to find
 * a resource-gated control in the shell — which is how the seam stayed unverified before.
 *
 * `orgRoles` / `orgPermissions` genuinely ARE DP-only concepts and stay empty on CP.
 * `resourceGrants` is NOT — CP owns `cp_auth.resource_grants` and the rows are live. A `null`
 * (not yet known, or unusable) collapses to `[]` HERE, which is correct for permission
 * *checks* (fail closed); the zero-access panel receives the un-collapsed value and is what
 * distinguishes "unknown" from "none".
 */
export function buildPermissionSnapshot(
  siteRoles: readonly string[],
  sitePermissions: readonly string[],
  grants: readonly ResourceGrantSnapshot[] | null,
): PermissionSnapshot {
  return {
    siteRoles,
    sitePermissions,
    orgRoles: [],
    orgPermissions: [],
    resourceGrants: grants ?? [],
  };
}
